import { EntityRatingCommentCount, Laboratory } from '@medprev-web-app/api-graphql-client';

import { mapSearchApiLaboratory } from './map-search-api-laboratory.util';

import {
  mapSearchApiCommentCount,
  mapSearchApiRatingComment,
} from '@/app/(main)/(pages)/_services/search-api/map-search-api-rating.util';
import {
  findFacilityBySlug,
  findFacilityRatingById,
  findFacilityRatingCommentsById,
  findPartnerById,
} from '@/app/(main)/(pages)/_services/search-api/search-api.service';
import {
  SearchApiPartner,
  SearchApiRating,
  SearchApiRatingCommentsOrderBy,
} from '@/app/(main)/(pages)/_types/search-api-response.type';
import { RatingComment } from '@/types';
import { getLogger } from '@/logger/logger';

const logger = getLogger('laboratory.profile');

export const LABORATORY_PROFILE_COMMENTS_PAGE_SIZE = 5;

export interface LaboratoryProfileFromSearchApi {
  laboratory: Laboratory;
  commentsCount: EntityRatingCommentCount;
  initialComments: RatingComment[];
  hasMoreComments: boolean;
}

export interface GetLaboratoryProfileCommentsResult {
  comments: RatingComment[];
  hasMore: boolean;
}

export async function getLaboratoryProfileFromSearchApi(slug: string): Promise<LaboratoryProfileFromSearchApi> {
  const facility = await findFacilityBySlug(slug);

  const [partner, rating, comments] = await Promise.all([
    findPartnerOrNull(facility.partnerId ?? facility.partner?.id),
    findRatingOrNull(facility.id),
    findFirstCommentsPageOrEmpty(facility.id),
  ]);

  return {
    laboratory: mapSearchApiLaboratory(facility, partner, rating),
    commentsCount: mapSearchApiCommentCount(rating),
    initialComments: comments.comments,
    hasMoreComments: comments.hasMore,
  };
}

export async function getLaboratoryProfileComments(
  partnerFacilityId: string,
  page: number,
  orderBy: SearchApiRatingCommentsOrderBy = SearchApiRatingCommentsOrderBy.NEWEST,
): Promise<GetLaboratoryProfileCommentsResult> {
  const { comments, hasMore } = await findFacilityRatingCommentsById(partnerFacilityId, {
    page,
    limit: LABORATORY_PROFILE_COMMENTS_PAGE_SIZE,
    orderBy,
  });

  return { comments: comments.map((comment) => mapSearchApiRatingComment(comment)), hasMore };
}

async function findFirstCommentsPageOrEmpty(
  partnerFacilityId: string,
): Promise<GetLaboratoryProfileCommentsResult> {
  try {
    return await getLaboratoryProfileComments(partnerFacilityId, 1);
  } catch (error) {
    logger.warn('Failed to fetch the laboratory comments', {
      laboratory: { event: 'comments_failed', partner_facility_id: partnerFacilityId },
      error,
    });

    return { comments: [], hasMore: false };
  }
}

async function findPartnerOrNull(partnerId?: string): Promise<SearchApiPartner | null> {
  if (!partnerId) {
    return null;
  }

  try {
    return await findPartnerById(partnerId);
  } catch (error) {
    logger.warn('Failed to fetch the partner of the laboratory', {
      laboratory: { event: 'partner_failed', partner_id: partnerId },
      error,
    });

    return null;
  }
}

async function findRatingOrNull(partnerFacilityId: string): Promise<SearchApiRating | null> {
  try {
    return await findFacilityRatingById(partnerFacilityId);
  } catch (error) {
    logger.warn('Failed to fetch the laboratory rating', {
      laboratory: { event: 'rating_failed', partner_facility_id: partnerFacilityId },
      error,
    });

    return null;
  }
}
